import { createFileRoute } from "@tanstack/react-router";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import { OS, getOS, isSafari } from "common/utils/context";
import { getGPUTier } from "detect-gpu";
import { benchmarkGpu } from "gfx/benchmarkGpu";
import { initTHREEJSRenderer } from "gfx/initPhysicallyBasedRenderer";
import { initWebGLContext } from "gfx/initWebGLContext";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useState } from "react";
import type { JSX } from "react";
import { Helmet as Head } from "react-helmet-async";

const PlatformDiagnosticsPage = (): JSX.Element => {
  const [rendererInfo, setRendererInfo] = useState<string>("");
  const [webglVersionInfo, setWebglVersionInfo] = useState<string>("");
  const [isSafariInfo, setIsSafariInfo] = useState<string>("");
  const [osInfo, setOsInfo] = useState<string>("");
  const [gpuTierFpsInfo, setGpuTierFpsInfo] = useState<string>("");
  const [gpuBenchmarkFpsInfo, setGpuBenchmarkFpsInfo] = useState<string>("");
  const [definedWindow, setWindow] = useState<any>();

  useEffect(() => {
    try {
      setWindow(window);
      setIsSafariInfo("Is Safari: " + isSafari());
      const os = getOS();
      assertIsNotUndefined(os);
      setOsInfo("OS: " + OS[os]);

      const contextInfo = initWebGLContext({
        framebufferOptions: {
          antialias: false,
          depthStencil: false,
        },
        allowWebGL1: true,
        throwOnGLError: false,
        logGLResourceUsage: false,
        label: "PlatformDiagnostics",
      });
      if (contextInfo.hasWebGL2) {
        setWebglVersionInfo("WebGL 2.0");
      } else {
        setWebglVersionInfo("WebGL 1.0");
      }
      const ext = contextInfo.glContext.getExtension("WEBGL_debug_renderer_info");
      let unmaskedRenderer;
      if (!ext) {
        setRendererInfo("Renderer: WEBGL_debug_renderer_info not supported");
      } else {
        unmaskedRenderer = contextInfo.glContext.getParameter(ext.UNMASKED_RENDERER_WEBGL);
        setRendererInfo("Renderer: " + unmaskedRenderer);
      }

      if (!contextInfo.hasWebGL2) {
        setGpuBenchmarkFpsInfo("Skipping benchmarks on WebGL 1.0");
        setGpuTierFpsInfo("Skipping benchmarks on WebGL 1.0");
      } else if (unmaskedRenderer !== undefined && /swiftshader/i.test(unmaskedRenderer)) {
        setGpuBenchmarkFpsInfo("Skipping benchmarks on SwiftShader");
        setGpuTierFpsInfo("Skipping benchmarks on SwiftShader");
      } else {
        const renderer = initTHREEJSRenderer(
          contextInfo.canvas,
          contextInfo.glContext,
          contextInfo.glContextAttributes,
        );
        const fps = benchmarkGpu(renderer);
        setGpuBenchmarkFpsInfo("Benchmark: " + fps + " fps");

        void (async () => {
          const gpuInfo = await getGPUTier({ benchmarksURL: "/static/benchmarks" });
          setGpuTierFpsInfo("detect-gpu estimate: " + gpuInfo.fps + " fps");
        })();
      }
    } catch (e) {
      setWebglVersionInfo("WebGL not supported");
    }
  }, []);

  return (
    <>
      <Head>
        <title>{getPageTitle("Platform Diagnostics")}</title>
      </Head>
      <style>
        {`
        .rawInfo, .platformInfo, .glInfo, .benchmarkInfo {
          margin: 20px;
        }`}
      </style>
      <div className="rawInfo">
        <div className="infoString">window.navigator.userAgent: {definedWindow?.navigator.userAgent}</div>
        <div className="infoString">window.navigator.platform: {definedWindow?.navigator.platform}</div>
        <div className="infoString">window.navigator.vendor: {definedWindow?.navigator.vendor}</div>
      </div>
      <div className="platformInfo">
        <div className="infoString">{osInfo}</div>
        <div className="infoString">{isSafariInfo}</div>
      </div>
      <div className="glInfo">
        <div className="infoString">{webglVersionInfo}</div>
        <div className="infoString">{rendererInfo}</div>
      </div>
      <div className="benchmarkInfo">
        <div className="infoString">{gpuTierFpsInfo}</div>
        <div className="infoString">{gpuBenchmarkFpsInfo}</div>
      </div>
    </>
  );
};

export const Route = createFileRoute("/platform-diagnostics")({
  component: PlatformDiagnosticsPage,
});
